import React, { Suspense, lazy } from 'react';
import { Amplify } from 'aws-amplify';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { awsConfig } from './config/aws';
import ProtectedRoute from './components/shared/ProtectedRoute';
import BottomNav from './components/shared/BottomNav';
// Eager: first-paint routes
import LandingPage from './pages/LandingPage';
import TodayPage from './pages/TodayPage';
import ScrollToTop from './components/shared/ScrollToTop';
import ErrorBoundary from './components/shared/ErrorBoundary';
import PageTransition from './components/shared/PageTransition';
import { useAuth } from './contexts/AuthContext';
import './styles/global.css';
// Lazy: everything else (code-splitting for faster initial load)
const SignupPage = lazy(() => import('./pages/SignupPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const FourPillarsPage = lazy(() => import('./pages/FourPillarsPage'));
const AvatarForgePage = lazy(() => import('./pages/AvatarForgePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const BirthDataPage = lazy(() => import('./pages/BirthDataPage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const TarotPage = lazy(() => import('./pages/TarotPage'));
const SacredCalendarPage = lazy(() => import('./pages/SacredCalendarPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const OraclePage = lazy(() => import('./pages/OraclePage'));
const OracleSettingsPage = lazy(() => import('./pages/OracleSettingsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const RelationshipReportPage = lazy(() => import('./pages/RelationshipReportPage'));
const CommissionPage = lazy(() => import('./pages/CommissionPage'));
const ParentingReportPage = lazy(() => import('./pages/ParentingReportPage'));
const FutureAssessmentPage = lazy(() => import('./pages/FutureAssessmentPage'));
const CirclePage = lazy(() => import('./pages/CirclePage'));
const RelationshipStoryPage = lazy(() => import('./pages/RelationshipStoryPage'));
const BondInsightsPage = lazy(() => import('./pages/BondInsightsPage'));
const HousesPage = lazy(() => import('./pages/HousesPage'));
const TimelinePage = lazy(() => import('./pages/TimelinePage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ArtistPortalPage = lazy(() => import('./pages/ArtistPortalPage'));
const EssencePage = lazy(() => import('./pages/EssencePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const GoPage = lazy(() => import('./pages/GoPage'));
const UpsellPage = lazy(() => import('./pages/UpsellPage'));
const GreekTempleTestPage = lazy(() => import('./pages/GreekTempleTestPage'));

Amplify.configure(awsConfig);

const ZealLoader: React.FC = () => (
  <div style={{
    position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', flexDirection: 'column', background: '#0a0a0f', zIndex: 9999,
  }}>
    <style>{`
      @keyframes zeal-orbit { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      @keyframes zeal-breathe { 0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(201,168,76,0.15); } 50% { transform: scale(1.08); box-shadow: 0 0 60px rgba(201,168,76,0.3); } }
    `}</style>
    <div style={{ position: 'relative', width: '140px', height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: '-10px', border: '1px solid rgba(201,168,76,0.1)', borderTopColor: 'rgba(201,168,76,0.4)', borderRadius: '50%', animation: 'zeal-orbit 3s linear infinite' }} />
      <img src="/app-icon.jpg" alt="Zeal Genesis" style={{ width: '90px', height: '90px', borderRadius: '50%', border: '2px solid rgba(201,168,76,0.3)', animation: 'zeal-breathe 2.5s ease-in-out infinite' }} />
    </div>
    <p style={{ marginTop: '28px', fontFamily: "Cinzel, Georgia, serif", fontSize: '20px', color: '#c9a84c', letterSpacing: '3px' }}>ZEAL GENESIS™</p>
  </div>
);

const HomeRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show Zeal loading screen while checking auth
  if (isLoading) {
    return <ZealLoader />;
  }

  if (isAuthenticated) return <TodayPage />;
  return <LandingPage />;
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
    <Router>
      <ScrollToTop />
      <ErrorBoundary>
      <AuthProvider>
      <SubscriptionProvider>
        <PageTransition>
        <Suspense fallback={<ZealLoader />}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/profile/four-pillars" element={<ProtectedRoute><FourPillarsPage /></ProtectedRoute>} />
          <Route path="/p/:userId" element={<PublicProfilePage />} />
          <Route path="/s/:shareId" element={<SharePage />} />
          <Route path="/go" element={<GoPage />} />
          <Route path="/premium" element={<UpsellPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/temple-test" element={<GreekTempleTestPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/birth-data"
            element={
              <ProtectedRoute>
                <BirthDataPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avatar-forge"
            element={
              <ProtectedRoute>
                <AvatarForgePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avatar-forge-commission"
            element={
              <ProtectedRoute>
                <CommissionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <TodayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tarot"
            element={
              <ProtectedRoute>
                <TarotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute>
                <SacredCalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/oracle"
            element={
              <ProtectedRoute>
                <OraclePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/oracle/settings"
            element={
              <ProtectedRoute>
                <OracleSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/relationship"
            element={
              <ProtectedRoute>
                <RelationshipReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/parenting"
            element={
              <ProtectedRoute>
                <ParentingReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/future"
            element={
              <ProtectedRoute>
                <FutureAssessmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/circle"
            element={
              <ProtectedRoute>
                <CirclePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/circle/:connectionId/bond-insights"
            element={
              <ProtectedRoute>
                <BondInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/circle/:connectionId/story"
            element={
              <ProtectedRoute>
                <RelationshipStoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/houses"
            element={
              <ProtectedRoute>
                <HousesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journey"
            element={
              <ProtectedRoute>
                <TimelinePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/essence"
            element={
              <ProtectedRoute>
                <EssencePage />
              </ProtectedRoute>
            }
          />
          <Route path="/artist" element={<ArtistPortalPage />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
        </PageTransition>
        <BottomNav />
      </SubscriptionProvider>
      </AuthProvider>
      </ErrorBoundary>
    </Router>
    </HelmetProvider>
  );
};

export default App;
